// src/layouts/BottomDrawer/tabs/VoipTab/AudioPlayer.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { CaretRightOutlined, PauseOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import axiosFetch from 'configs/fetchInterceptor';
import { API } from 'constants/Api';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AudioPlayer = props => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { callId } = props;

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  // Cleanup: pause audio and revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handlePlayPause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || loading) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }

    // If blob URL already loaded, just play
    if (blobUrlRef.current) {
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        setError(true);
      }
      return;
    }

    // Fetch audio via backend proxy (handles Firebase Storage auth)
    setLoading(true);
    setError(false);
    try {
      const audioUrl = `${API.GOTO.CALLS_AUDIO.replace(':_id', callId)}`;
      const baseURL = axiosFetch.defaults.baseURL || '';
      const fullUrl = `${baseURL}${audioUrl}`;

      // Include auth token from axios default headers
      const authHeader = axiosFetch.defaults.headers?.common?.Authorization
        || axiosFetch.defaults.headers?.Authorization
        || '';

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(fullUrl, {
        headers: authHeader ? { Authorization: authHeader } : {},
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = blobUrl;
      audio.src = blobUrl;

      await audio.play();
      setPlaying(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [playing, loading, callId]);

  const handleStop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    setPlaying(false);
  }, []);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  if (!callId) return null;

  if (error) {
    return (
      <Tooltip title="Erro ao carregar áudio">
        <WarningOutlined style={{ color: 'var(--ant-color-error)', fontSize: 14 }} />
      </Tooltip>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      <audio ref={audioRef} preload="none" onEnded={handleEnded} />
      <Button
        type="text"
        size="small"
        icon={playing ? <PauseOutlined /> : <CaretRightOutlined />}
        onClick={handlePlayPause}
        loading={loading}
        style={{ color: 'var(--ant-color-primary)' }}
      />
      {playing && (
        <Button
          type="text"
          size="small"
          icon={<StopOutlined />}
          onClick={handleStop}
          style={{ color: 'var(--ant-color-text-secondary)' }}
        />
      )}
    </span>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AudioPlayer;
